import React, { useMemo } from "react";
import dynamic from "next/dynamic";
import { useParcels } from "@/context/Parcels.context";

import { Modal, DeleteAlert, ShowWhen, Loader } from "@/components/common";
import ViewGetParcel from "./view-parcel/view-get-parcel";
import { ModalType, ParcelStatus, Theme } from "@/enums";
import { usePathname } from "next/navigation";
import ViewSentParcel from "./view-parcel/view-sent-parcel";
import { useDeleteShipmentRequest } from "@/api/useDeleteShipmentRequest";
import { useTheme } from "@/context/Theme.context";
import { useGetViewParcelRequest } from "@/api/useGetViewParcelRequest";
import { declarationRequiredStatusses } from "@/utils/customUtils";

const AddParcelFormUz = dynamic(
  () => import("./parcel-form-components/uz/add-parcel-uz"),
  { loading: () => <Loader />, ssr: false },
);
const AddParcelFormGE = dynamic(
  () => import("./parcel-form-components/ge/add-parcel-ge"),
  { loading: () => <Loader />, ssr: false },
);
const AddParcelFormUA = dynamic(
  () => import("./parcel-form-components/ua/add-parcel-ua"),
  { loading: () => <Loader />, ssr: false },
);

const ParcelsModal = () => {
  const theme = useTheme();
  const {
    parcelId,
    openParcelsModal,
    modalOpen,
    modalType,
    closeModal,
    parcelStatus,
  } = useParcels();

  const pathname = usePathname();

  const isSendParcel = pathname.includes("send-parcel");

  const { data } = useGetViewParcelRequest(parcelId);

  const currentParcel = data?.data?.data || null;

  const deleteShipment = useDeleteShipmentRequest();

  const deleteParcel = async () => {
    if (parcelId) {
      const deleteShipmentResponse = await deleteShipment({
        shipmentId: parcelId,
      });
      if (deleteShipmentResponse?.data?.data?.completed) {
        closeModal();
      }
    }
  };

  const renderModalContent = useMemo(() => {
    switch (modalType) {
      case ModalType.ADD:
      case ModalType.EDIT:
        return (
          <ShowWhen
            condition={theme === Theme.UZ}
            elseRender={
              <ShowWhen
                condition={theme === Theme.GEORGIA}
                elseRender={<AddParcelFormUA />}
              >
                <AddParcelFormGE />
              </ShowWhen>
            }
          >
            <AddParcelFormUz />
          </ShowWhen>
        );
      case ModalType.VIEW:
        return (
          <ShowWhen
            condition={!isSendParcel}
            elseRender={<ViewSentParcel parcelId={parcelId} />}
          >
            <ViewGetParcel currentParcel={currentParcel} parcelId={parcelId} />
          </ShowWhen>
        );
      case ModalType.DELETE:
        return (
          <DeleteAlert
            title="delete_parcel_warning"
            closeModal={closeModal}
            confirmDelete={deleteParcel}
          />
        );
      default:
        return;
    }
  }, [modalType, theme, currentParcel]);

  const isEditable = useMemo(() => {
    const isDeclared = currentParcel?.isDeclared;

    const allowedStatussesForUndeclaredParcels = [
      ParcelStatus.COMMON_STATUS_PROCESSED_AT_THE_INSTOCK,
      ParcelStatus.COMMON_STATUS_PREPARED_SHIPPING_FROM_FCOUNTRY,
      ParcelStatus.COMMON_STATUS_SHIPPED_FROM_FCOUNTRY,
    ];

    if (
      parcelStatus === ParcelStatus.COMMON_STATUS_STATUS_WAITING_TO_RECEIVE ||
      parcelStatus === ParcelStatus.COMMON_STATUS_RECEIVED_AT_THE_INSTOCK
    ) {
      return true;
    }

    if (!isDeclared) {
      if (allowedStatussesForUndeclaredParcels.includes(parcelStatus)) {
        return true;
      }
    }

    return false;
  }, [currentParcel, parcelStatus]);

  return (
    <Modal
      title={modalType === ModalType.VIEW ? "parcel_details" : ""}
      showActions={modalType === ModalType.VIEW}
      showModalHeader={
        modalType === ModalType.ADD || modalType === ModalType.EDIT
      }
      className={modalType === ModalType.VIEW ? "h-full min-w-[36.75rem]" : ""}
      isOpen={modalOpen}
      closeModal={closeModal}
      fullScreen={modalType === ModalType.ADD || modalType === ModalType.EDIT}
      onEdit={() => {
        openParcelsModal({
          modalType: ModalType.EDIT,
        });
      }}
      showEdit={isEditable}
      showDelete={
        parcelStatus === ParcelStatus.COMMON_STATUS_STATUS_WAITING_TO_RECEIVE
      }
      onDelete={() => {
        openParcelsModal({
          modalType: ModalType.DELETE,
        });
      }}
      needsDeclaration={
        declarationRequiredStatusses.includes(parcelStatus) &&
        currentParcel?.isDeclared === false
      }
    >
      {renderModalContent}
    </Modal>
  );
};

export default ParcelsModal;
